import { Inject, Injectable, Renderer2 } from '@angular/core';
import { EnvironmentService } from './environment/environment.service';
import { APP_CONST } from '@assets/const/app.const';
import { MfeAppConfig } from '../app.environment';
import { DOCUMENT } from '@angular/common';
import { LoadedMfeHelper } from './loaded-mfe.helper';

@Injectable({
  providedIn: 'root'
})
export class StylesService {
  readonly styleRefsByAppPath = new Map<string, string>();

  constructor(
    private readonly environmentService: EnvironmentService,
    @Inject(DOCUMENT) private ngDocument: Document
  ) {}

  switchColorsForLoadedMfe(): void {
    const loadedApp = LoadedMfeHelper.getLoadedApp();
    const apps = this.environmentService.getApps();
    const app =
      loadedApp && apps[loadedApp]
        ? apps[loadedApp]
        : this.environmentService.getDefaultApp();

    if (window.location.pathname == APP_CONST.TERMS_AND_CONDITIONS_PATH) {
      const { INTERACTIVE, FOCUS } = APP_CONST.DEFAULT_COLORS;
      app.color['interactive'] = INTERACTIVE;
      app.color['focus'] = FOCUS;
    }

    StylesService.setStyleProp(
      '--color-interactive-01',
      app.color['interactive'] ?? app.color.primary
    );
    StylesService.setStyleProp(
      '--color-focus-01',
      app.color['focus'] ?? app.color.primary
    );
  }

  applySubmenuStyleFix(mfeApps: MfeAppConfig[]) {
    if (mfeApps.length <= 2) {
      document.querySelector('app-header')?.classList.add('apply-submenu-fix');
    }
  }

  // Renderer2 can't be injected to services
  applyColorStyles(mfeApps: MfeAppConfig[], renderer: Renderer2) {
    // TODO The original code includes a bug where the styles are added multiple times. This should be addressed in the future
    let dynamicStylesheet = '';
    for (const mfeApp of mfeApps) {
      dynamicStylesheet =
        dynamicStylesheet +
        `.cds-nav-item.-selected #cds-nav-item-${mfeApp.path} {
        --color-interactive-01:${mfeApp.color.primary}!important;
        color:${mfeApp.color.primary}!important;
      }
        `;
    }

    dynamicStylesheet = `#cds-navigation-menu-nav {
      ${dynamicStylesheet}
    }
      `;

    this.addStyleSheet(dynamicStylesheet, renderer);
  }

  loadCss(path: string, url: string): Promise<void> {
    // TODO Don't use async inside a promise
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const appConfig = this.environmentService
        .getAppsArray()
        .find((app) => app.path === path);

      if (!this.getStyleRef(appConfig)) {
        await this.loadStyleUrls();
      }

      if (!appConfig) {
        resolve();
        return;
      }

      // Update the styles link element only when necessary
      const existingMfeStyleLinks =
        document.getElementsByClassName('mfe-styles');

      // Remove links that do not match the current path
      for (let i = existingMfeStyleLinks.length - 1; i >= 0; i--) {
        const link = existingMfeStyleLinks[i] as HTMLLinkElement;
        if (link.id !== `${path}-styles`) {
          link.remove();
        }
      }

      // Now check if the link we want to add already exists
      const newLinkId = `${path}-styles`;
      const existingLink = document.getElementById(
        newLinkId
      ) as HTMLLinkElement;

      if (existingLink) {
        // If the link exists, check if it matches the latest styleRef
        if (existingLink.href !== `${this.getStyleRef(appConfig)}`) {
          // Update the href if it does not match
          existingLink.href = `${this.getStyleRef(appConfig)}`;
          existingLink.onload = () => {
            resolve();
          };
        } else {
          // Resolve immediately if the href matches
          return resolve();
        }
      } else {
        // Create and append a new link element
        const link = document.createElement('link');
        link.id = newLinkId;
        link.className = 'mfe-styles';
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = `${this.getStyleRef(appConfig)}`;
        link.onload = () => {
          resolve();
        };
        link.onerror = () => reject(new Error(`Failed to load CSS: ${path}`));
        document.head.appendChild(link);
      }

      if (url == APP_CONST.TERMS_AND_CONDITIONS_PATH) {
        const style = document.createElement('style');
        style.textContent = `
          :root,
          cds-loading-spinner {
            --color-interactive-01: #0e57cc !important;
            --color-focus-01: #b6ccef !important;
            --color-hover-primary: red !important;
          }
        `;
        document.head.appendChild(style);
      }
    });
  }

  async loadStyleUrls() {
    for (const app of this.environmentService.getLoadedApps()) {
      if (app.title) {
        const url = `${app.hostname}/assets/${app.path}/configs/file-manifest.json`;
        const fileManifestResponse = await fetch(url);
        const fileManifest = await fileManifestResponse.json();
        this.styleRefsByAppPath.set(
          app.path,
          `${app.hostname}/${fileManifest.files.styles}`
        );
      }
    }
  }

  removeAdditionalCSS(url: string) {
    const styles = this.ngDocument.querySelectorAll('.mfe-styles');
    styles.forEach((style) => {
      if (!style.id.startsWith(url)) {
        style?.remove();
      }
    });
  }

  private addStyleSheet(rawStyleSheet: string, renderer: Renderer2) {
    const styleElement = renderer.createElement('style') as HTMLElement;
    const styleTextElement = renderer.createText(rawStyleSheet);
    renderer.appendChild(styleElement, styleTextElement);
    renderer.appendChild(document.head, styleElement);
  }

  private getStyleRef(app: MfeAppConfig | undefined): string | undefined {
    return app ? this.styleRefsByAppPath.get(app.path) : undefined;
  }

  private static setStyleProp(variableName: string, value: string) {
    return document.documentElement.style.setProperty(
      variableName,
      value,
      'important'
    );
  }
}
